// Intermediate, part 1: the desk's trend-pullback setup, read on real charts.
// Each game is one line of the engine's checklist (backend
// signals/setupChecklist.ts, setupClassifier.ts; the numbers live in
// ../rules.ts): the 4h bias, going with it, the pullback zone, a real
// pullback, the trigger candle, the stop, the targets, the RSI band and the
// trigger's volume. Answers come from the candles up to "now" only.
import type { Candle, ChartMark, ChartSpec, ChartZone, GameData, GameDef, Option, Rng } from "../types";
import { atr, biasCheck, emaLast, pctChange, pctText, planOutcome, price, rText, rsiLast, volumeRatio, type BiasCheck } from "../helpers";
import { BIAS_CANDLES } from "../../chart/structure";
import {
  body,
  isBearish,
  isBullish,
  isHammer,
  isShootingStar,
  lowerWick,
  PULLBACK_ATR,
  pullbackStop,
  reactionTrigger,
  RSI_BAND,
  STOP_BUFFER_ATR,
  STOP_LOOKBACK,
  TP1_FALLBACK_R,
  TP2_R,
  upperWick,
  VOLUME_MIN,
  VOLUME_STRONG,
  ZONE_ATR,
  type Trigger,
} from "../rules";

// ── Shared reads ────────────────────────────────────────────────────────────

/** The 1h trend the engine reads: EMA 20 vs EMA 50, and ATR (the average candle size). */
export interface TrendRead {
  e20: number;
  e50: number;
  a: number;
  isLong: boolean;
  /** |EMA 20 − EMA 50| in ATR: under 0.1 the order is too close to see. */
  gap: number;
}

export function trendOf(candles: Candle[]): TrendRead | null {
  const e20 = emaLast(candles, 20);
  const e50 = emaLast(candles, 50);
  const a = atr(candles);
  if (e20 === null || e50 === null || a === null || !(a > 0)) return null;
  return { e20, e50, a, isLong: e20 > e50, gap: Math.abs(e20 - e50) / a };
}

/** Plain words for a side. */
function sideWords(isLong: boolean) {
  return isLong
    ? { buy: "buy", BUY: "BUY", trend: "uptrend", aTrend: "an uptrend", order: "EMA 20 above EMA 50", high: "high", low: "low" }
    : { buy: "sell", BUY: "SELL", trend: "downtrend", aTrend: "a downtrend", order: "EMA 20 below EMA 50", high: "low", low: "high" };
}

/** The first candle a "search back" may stop at: EMA 50 plus some room. */
const FIRST_MOMENT = 59;

interface Moment<T> {
  i: number;
  shown: Candle[];
  read: T;
}

/**
 * Search back through the history for moments that make a fair question
 * (`read` sees only the candles up to that moment), and let the rng pick one,
 * preferring the ones `wanted` likes (so every answer turns up).
 */
function pickMoment<T>(data: GameData, rng: Rng, read: (shown: Candle[]) => T | null, wanted: (t: T) => boolean, from = FIRST_MOMENT): Moment<T> | null {
  const found: Moment<T>[] = [];
  for (let i = Math.max(1, from); i < data.candles.length; i++) {
    const shown = data.candles.slice(0, i + 1);
    const r = read(shown);
    if (r !== null) found.push({ i, shown, read: r });
  }
  if (found.length === 0) return null;
  const good = found.filter(m => wanted(m.read));
  return rng.pick(good.length > 0 ? good : found);
}

/** The chart cut at candle `i`: the candles up to it, and the `ahead` after it for the reveal. */
function chartAt(data: GameData, i: number, ahead: number, extra: Omit<ChartSpec, "tf" | "candles" | "ahead"> = {}): ChartSpec {
  return {
    tf: data.tf,
    candles: data.candles.slice(0, i + 1),
    ahead: [...data.candles.slice(i + 1), ...data.ahead].slice(0, ahead),
    ...extra,
  };
}

/** "Over the next 12 candles price went from 142.10 to 145.80 (+2.60%)." */
function moveOutcome(from: number, ahead: Candle[] | undefined, unit = "candles"): string | undefined {
  const end = ahead?.[ahead.length - 1];
  if (!ahead || !end) return undefined;
  const p = pctChange(from, end.close);
  return `Over the next ${ahead.length} ${unit} price went from ${price(from)} to ${price(end.close)} (${p >= 0 ? "+" : ""}${pctText(p)}).`;
}

const ATR_FACT = "ATR (average candle size)";

// ── 1. Read the 4h bias ─────────────────────────────────────────────────────

type Vote = "up" | "down" | null;
type Direction = BiasCheck["direction"];

/** The engine's tally of the three votes, added in its own order (so the float edge at ±0.2 lands the same). */
export function tally(ema: Vote, rsi: Vote, swing: Vote): { bull: number; bear: number; net: number; direction: Direction } {
  let bull = 0;
  let bear = 0;
  if (ema === "up") bull += 0.4;
  else if (ema === "down") bear += 0.4;
  if (rsi === "up") bull += 0.3;
  else if (rsi === "down") bear += 0.3;
  else {
    bull += 0.1;
    bear += 0.1;
  }
  if (swing === "up") bull += 0.3;
  else if (swing === "down") bear += 0.3;
  const net = bull - bear;
  return { bull, bear, net, direction: net > 0.2 ? "bullish" : net < -0.2 ? "bearish" : "neutral" };
}

/** Within this many ATR, two lines or two swings are too close to order by eye. */
const EYE_ATR = 0.1;

/** Can a run of swing prices be read as all rising (`up`) / all falling? Both when a step is too small to see. */
function runReads(prices: number[], up: boolean, tol: number): boolean[] {
  if (prices.length < 2) return [false];
  let canTrue = true;
  let canFalse = false;
  for (let k = 1; k < prices.length; k++) {
    const d = (prices[k]! - prices[k - 1]!) * (up ? 1 : -1);
    if (d < tol) canFalse = true;
    if (d <= -tol) canTrue = false;
  }
  return [...(canTrue ? [true] : []), ...(canFalse ? [false] : [])];
}

/** Every bias a player could fairly read from this chart (more than one = a close call). */
function readableDirections(b: BiasCheck, a: number): Set<Direction> {
  const tol = EYE_ATR * a;
  const e200 = b.ema.e200 ?? Number.NaN;
  const signs = (d: number) => (Math.abs(d) < tol ? [1, -1] : [Math.sign(d)]);
  const ema = new Set<Vote>([b.ema.vote]);
  for (const s1 of signs(b.ema.e20 - b.ema.e50)) for (const s2 of signs(b.ema.e50 - e200)) ema.add(s1 > 0 && s2 > 0 ? "up" : s1 < 0 && s2 < 0 ? "down" : null);
  // RSI is shown to one decimal: within 0.1 of 55 or 45 it could read either way.
  const r = b.rsi.value;
  const rsi = new Set<Vote>([b.rsi.vote]);
  if (Math.abs(r - 55) < 0.1) { rsi.add("up"); rsi.add(null); }
  if (Math.abs(r - 45) < 0.1) { rsi.add("down"); rsi.add(null); }
  const swing = new Set<Vote>([b.swings.vote]);
  for (const rising of runReads(b.swings.highs.map(h => h.price), true, tol)) {
    for (const falling of runReads(b.swings.lows.map(l => l.price), false, tol)) swing.add(rising && !falling ? "up" : falling && !rising ? "down" : null);
  }
  const out = new Set<Direction>();
  for (const e of ema) for (const x of rsi) for (const s of swing) out.add(tally(e, x, s).direction);
  return out;
}

const tenths = (n: number) => (Math.round(n * 10) / 10).toFixed(1);
const signedTenths = (n: number) => `${n > 0.0001 ? "+" : n < -0.0001 ? "−" : ""}${tenths(Math.abs(n))}`;
const run = (xs: { price: number }[]) => xs.map(x => price(x.price)).join(" → ");

export const fourHourBias: GameDef = {
  id: "four-hour-bias",
  level: "intermediate",
  title: "Read the 4h bias",
  skill: "Add up the desk's three 4h votes (EMA order, RSI, swings) into bullish, bearish or neutral.",
  needs: { tf: "4h", history: 300, ahead: 30 },
  make(data) {
    if (!data) return null;
    // The engine reads the last 259 closed 4h candles; the chart shows the same ones so its EMAs match.
    const window = data.candles.slice(-BIAS_CANDLES);
    const b = biasCheck(window);
    const a = atr(window);
    if (!b || b.ema.e200 === null || a === null || !(a > 0)) return null;
    // The floating-point edge: a net of exactly ±0.2 lands on whichever side the float rounding falls.
    if (Math.abs(b.confidence - 0.2) < 0.01) return null;
    const dirs = readableDirections(b, a);
    if (dirs.size !== 1 || !dirs.has(b.direction)) return null;
    const t = tally(b.ema.vote, b.rsi.vote, b.swings.vote);
    const { e20, e50 } = b.ema;
    const e200 = b.ema.e200;
    const r = b.rsi.value;

    const emaText = b.ema.vote === "up"
      ? `EMAs stacked up (20 at ${price(e20)} > 50 at ${price(e50)} > 200 at ${price(e200)}): +0.4 up`
      : b.ema.vote === "down"
        ? `EMAs stacked down (20 at ${price(e20)} < 50 at ${price(e50)} < 200 at ${price(e200)}): +0.4 down`
        : `EMAs not stacked in order (20 at ${price(e20)}, 50 at ${price(e50)}, 200 at ${price(e200)}): no vote`;
    const rsiText = b.rsi.vote === "up" ? `RSI ${r.toFixed(1)} is over 55: +0.3 up`
      : b.rsi.vote === "down" ? `RSI ${r.toFixed(1)} is under 45: +0.3 down`
        : `RSI ${r.toFixed(1)} is between 45 and 55: +0.1 to each side`;
    const { highs, lows } = b.swings;
    const swingText = b.swings.vote === "up" ? `the last swing highs keep rising (${run(highs)}): +0.3 up`
      : b.swings.vote === "down" ? `the last swing lows keep falling (${run(lows)}): +0.3 down`
        : `the swings are mixed (highs ${run(highs)}, lows ${run(lows)}): no vote`;

    const name = { bullish: "Bullish", bearish: "Bearish", neutral: "Neutral" } as const;
    const score = `up ${tenths(t.bull)} vs down ${tenths(t.bear)}, net ${signedTenths(t.net)}`;
    const options: Option[] = [
      { id: "bullish", label: "Bullish", why: `Bullish needs up to beat down by more than 0.2: here it's ${score}.` },
      { id: "bearish", label: "Bearish", why: `Bearish needs down to beat up by more than 0.2: here it's ${score}.` },
      { id: "neutral", label: "Neutral", why: `Neutral is a net between −0.2 and +0.2: here it's ${score}.` },
    ].map(o => (o.id === b.direction ? { id: o.id, label: o.label } : o));

    const answerMarks: ChartMark[] = [
      ...highs.map(h => ({ time: h.time, label: "H", tone: "muted" as const, position: "above" as const })),
      ...lows.map(l => ({ time: l.time, label: "L", tone: "muted" as const, position: "below" as const })),
    ];
    const last = window[window.length - 1]!;
    return {
      kind: "choice",
      prompt: "This is the 4h chart the desk reads for its bias. Add up the three votes (EMA order, RSI, the last swing highs and lows): what is the 4h bias?",
      facts: [{ label: "RSI 14 (4h)", value: r.toFixed(1) }],
      chart: { tf: "4h", candles: window, ahead: data.ahead, focus: 100, emas: [20, 50, 200], answerMarks },
      options,
      answer: b.direction,
      explain: `The three votes: ${emaText}; ${rsiText}; ${swingText}. That's ${score}, so ${name[b.direction].toLowerCase()} (over +0.2 is bullish, under −0.2 bearish, anything between neutral).`,
      outcome: moveOutcome(last.close, data.ahead, "4h candles"),
    };
  },
};

// ── 2. With the 4h bias? ────────────────────────────────────────────────────

export const withTheBias: GameDef = {
  id: "with-the-bias",
  level: "intermediate",
  title: "With the 4h bias?",
  skill: "Take a trend-pullback trade only in the direction of the 4h bias.",
  needs: { tf: "1h", history: 150, ahead: 24, htf: true },
  make(data, rng) {
    if (!data?.htf) return null;
    const b = biasCheck(data.htf);
    if (!b) return null;
    const isBuy = rng.chance(0.5);
    const w = sideWords(isBuy);
    const last = data.candles[data.candles.length - 1]!;
    const answer = b.direction === "neutral" ? "neutral" : (b.direction === "bullish") === isBuy ? "with" : "against";
    const biasName = b.direction;
    const goesWith = isBuy ? "bullish" : "bearish";
    const options: Option[] = [
      {
        id: "with",
        label: "Yes: it goes with the 4h bias",
        why: answer === "neutral" ? "A neutral 4h bias doesn't back either side." : `The 4h bias is ${biasName}, so a ${w.buy} fights the bigger trend.`,
      },
      {
        id: "against",
        label: "No: it goes against the 4h bias",
        why: answer === "neutral" ? "A neutral 4h isn't against the trade, but it doesn't back it either." : `A ${biasName} 4h and a ${w.buy} point the same way.`,
      },
      { id: "neutral", label: "No: the 4h bias is neutral, so the desk waits", why: `The 4h bias is ${biasName}, not neutral.` },
    ].map(o => (o.id === answer ? { id: o.id, label: o.label } : o));
    const explain = answer === "with"
      ? `The 4h bias is ${biasName} and this is a ${w.buy}: it goes with the bigger trend, so the checklist's first item ("4h bias ${goesWith}") is met.`
      : answer === "against"
        ? `The 4h bias is ${biasName} but this is a ${w.buy}: it fights the bigger trend, so the checklist's "4h bias" item fails and the desk skips it. It only ${w.buy}s when the 4h is ${goesWith}.`
        : `A neutral 4h bias backs neither side, so the "4h bias" item fails for buys and sells alike. The trend-pullback setup waits until the 4h picks a direction.`;
    return {
      kind: "choice",
      prompt: `The 1h chart lines up a trend-pullback ${w.BUY} at ${price(last.close)}. Does the desk take it?`,
      facts: [{ label: "4h bias", value: biasName[0]!.toUpperCase() + biasName.slice(1) }],
      chart: {
        tf: data.tf,
        candles: data.candles,
        ahead: data.ahead,
        focus: 60,
        emas: [20, 50],
        lines: [{ price: last.close, label: w.BUY, tone: isBuy ? "profit" : "loss", dashed: true }],
      },
      options,
      answer,
      explain,
      outcome: moveOutcome(last.close, data.ahead),
    };
  },
};

// ── 3. In the pullback zone? ────────────────────────────────────────────────

export interface ZoneRead {
  t: TrendRead;
  px: number;
  /** The nearer EMA and price's distance from it, in ATR. */
  near: 20 | 50;
  dist: number;
  inZone: boolean;
}

/** The checklist's "zone": price within 1.5 ATR of the EMA 20 or the EMA 50. */
export function zoneRead(candles: Candle[]): ZoneRead | null {
  const t = trendOf(candles);
  const last = candles[candles.length - 1];
  if (!t || !last) return null;
  const px = last.close;
  const d20 = Math.abs(px - t.e20);
  const d50 = Math.abs(px - t.e50);
  const near = d20 <= d50 ? 20 : 50;
  return { t, px, near, dist: Math.min(d20, d50) / t.a, inZone: d20 <= ZONE_ATR * t.a || d50 <= ZONE_ATR * t.a };
}

/** The zone as chart bands: one band, or two when the EMAs sit more than 3 ATR apart. */
function zoneBands(t: TrendRead): ChartZone[] {
  const r = ZONE_ATR * t.a;
  const lo = Math.min(t.e20, t.e50);
  const hi = Math.max(t.e20, t.e50);
  return hi - lo <= 2 * r
    ? [{ from: lo - r, to: hi + r, tone: "brand" }]
    : [{ from: lo - r, to: lo + r, tone: "brand" }, { from: hi - r, to: hi + r, tone: "brand" }];
}

export const inTheZone: GameDef = {
  id: "in-the-zone",
  level: "intermediate",
  title: "In the pullback zone?",
  skill: "Check that price has come back within 1.5 ATR of the EMA 20 or EMA 50 before looking for an entry.",
  needs: { tf: "1h", history: 220, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const want = rng.chance(0.5);
    const m = pickMoment(data, rng, shown => {
      const z = zoneRead(shown);
      if (!z || z.t.gap < 0.1 || Math.abs(z.dist - ZONE_ATR) < 0.15) return null;
      return z;
    }, z => z.inZone === want);
    if (!m) return null;
    const z = m.read;
    const { t, px } = z;
    const w = sideWords(t.isLong);
    const nearEma = z.near === 20 ? t.e20 : t.e50;
    const chart = chartAt(data, m.i, 12, { focus: 60, emas: [20, 50], answerZones: zoneBands(t) });
    const distText = `${z.dist.toFixed(2)} ATR`;
    return {
      kind: "choice",
      prompt: `This 1h chart is in ${w.aTrend} (${w.order}). Is the last close in the desk's pullback zone: within 1.5 ATR of the EMA 20 or the EMA 50?`,
      facts: [
        { label: "Last close", value: price(px) },
        { label: "EMA 20", value: price(t.e20) },
        { label: "EMA 50", value: price(t.e50) },
        { label: ATR_FACT, value: price(t.a) },
      ],
      chart,
      options: [
        { id: "yes", label: "Yes: it's within 1.5 ATR of an EMA", ...(z.inZone ? {} : { why: `Price is ${distText} from the nearer EMA, more than 1.5 ATR: it's still stretched.` }) },
        { id: "no", label: "No: it's too far from both EMAs", ...(z.inZone ? { why: `Price is only ${distText} from the EMA ${z.near}, inside 1.5 ATR.` } : {}) },
      ],
      answer: z.inZone ? "yes" : "no",
      explain: `The nearer line is the EMA ${z.near} at ${price(nearEma)}: price is ${price(Math.abs(px - nearEma))} away, ${distText} (ATR ${price(t.a)}). The zone reaches 1.5 ATR (${price(ZONE_ATR * t.a)}) from either EMA, so ${z.inZone ? `price is in it and the desk can look for a trigger candle` : `price is outside it and the desk waits instead of chasing`}.`,
      outcome: moveOutcome(px, chart.ahead),
    };
  },
};

// ── 4. Click the pullback zone ──────────────────────────────────────────────

export interface RunAway {
  t: TrendRead;
  px: number;
  zoneLow: number;
  zoneHigh: number;
  /** How far price is past the zone, in ATR (in the trend's direction). */
  past: number;
}

/** Price ran more than 1 ATR past the zone in the trend's direction (one joined zone, a clear trend). */
export function runAwayRead(candles: Candle[]): RunAway | null {
  const t = trendOf(candles);
  const last = candles[candles.length - 1];
  if (!t || !last || t.gap < 0.1 || t.gap > 2 * ZONE_ATR) return null;
  const zoneLow = Math.min(t.e20, t.e50) - ZONE_ATR * t.a;
  const zoneHigh = Math.max(t.e20, t.e50) + ZONE_ATR * t.a;
  const px = last.close;
  const past = (t.isLong ? px - zoneHigh : zoneLow - px) / t.a;
  return past > 1 ? { t, px, zoneLow, zoneHigh, past } : null;
}

export const clickTheZone: GameDef = {
  id: "click-the-zone",
  level: "intermediate",
  title: "Click the pullback zone",
  skill: "Find where the desk waits for a pullback when price has run away from its EMAs.",
  needs: { tf: "1h", history: 300, ahead: 24 },
  make(data, rng) {
    if (!data) return null;
    const m = pickMoment(data, rng, runAwayRead, () => true);
    if (!m) return null;
    const { t, px, zoneLow, zoneHigh, past } = m.read;
    const w = sideWords(t.isLong);
    const mid = (t.e20 + t.e50) / 2;
    const chart = chartAt(data, m.i, 24, {
      focus: 80,
      emas: [20, 50],
      answerZones: [{ from: zoneLow, to: zoneHigh, tone: "brand" }],
    });
    const ahead = chart.ahead ?? [];
    let outcome: string | undefined;
    if (ahead.length > 0) {
      const back = t.isLong ? ahead.some(c => c.low <= zoneHigh) : ahead.some(c => c.high >= zoneLow);
      outcome = back
        ? `Price came back into the zone within the next ${ahead.length} candles.`
        : `Price didn't come back to the zone in the next ${ahead.length} candles: no trade, and no chase.`;
    }
    return {
      kind: "price",
      prompt: `Price has run ${t.isLong ? "up and away" : "down and away"} from its EMAs in this ${w.trend}. Click the pullback zone where the desk waits to ${w.buy}.`,
      facts: [
        { label: "Last close", value: price(px) },
        { label: ATR_FACT, value: price(t.a) },
      ],
      chart,
      answer: mid,
      accept: { from: zoneLow, to: zoneHigh },
      explain: `The zone is the EMA 20 (${price(t.e20)}) and EMA 50 (${price(t.e50)}) with 1.5 ATR (${price(ZONE_ATR * t.a)}) either side: ${price(zoneLow)} – ${price(zoneHigh)}. Price at ${price(px)} is ${past.toFixed(2)} ATR past it, so the desk doesn't chase: it waits for price to come back. Anywhere in the zone counts.`,
      outcome,
    };
  },
};

// ── 5. A real pullback? ─────────────────────────────────────────────────────

export interface PullbackRead {
  t: TrendRead;
  /** The leg's extreme: the highest high (buy) of the 7 candles before the last. */
  extreme: number;
  extremeTime: number;
  /** The pullback's far point: the lowest low (buy) of the last 4 candles. */
  far: number;
  farTime: number;
  /** extreme − far (buy), in ATR. */
  depth: number;
  real: boolean;
}

/** The checklist's "pullback": price retraced at least 0.8 ATR from the recent extreme. */
export function pullbackRead(candles: Candle[]): PullbackRead | null {
  const t = trendOf(candles);
  if (!t || candles.length < 8) return null;
  const leg = candles.slice(-8, -1);
  const recent = candles.slice(-4);
  const pick = (xs: Candle[], key: "high" | "low", most: boolean) =>
    xs.reduce((best, c) => (most ? c[key] > best[key] : c[key] < best[key]) ? c : best);
  const ext = t.isLong ? pick(leg, "high", true) : pick(leg, "low", false);
  const far = t.isLong ? pick(recent, "low", false) : pick(recent, "high", true);
  const extreme = t.isLong ? ext.high : ext.low;
  const farPrice = t.isLong ? far.low : far.high;
  const depth = (t.isLong ? extreme - farPrice : farPrice - extreme) / t.a;
  return { t, extreme, extremeTime: ext.time, far: farPrice, farTime: far.time, depth, real: depth >= PULLBACK_ATR };
}

export const realPullback: GameDef = {
  id: "real-pullback",
  level: "intermediate",
  title: "A real pullback?",
  skill: "Tell a real pullback (at least 0.8 ATR from the recent extreme) from sideways drift.",
  needs: { tf: "1h", history: 220, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const want = rng.chance(0.5);
    const m = pickMoment(data, rng, shown => {
      const p = pullbackRead(shown);
      if (!p || p.t.gap < 0.1 || Math.abs(p.depth - PULLBACK_ATR) < 0.1) return null;
      // The engine doesn't check the order, so a steady climb also "retraces"
      // (its low comes before its high). Skip those: "the pullback reached …"
      // must be a move that came after the extreme.
      if (p.farTime < p.extremeTime) return null;
      return p;
    }, p => p.real === want);
    if (!m) return null;
    const p = m.read;
    const { t } = p;
    const w = sideWords(t.isLong);
    const last = m.shown[m.shown.length - 1]!;
    const move = Math.abs(p.extreme - p.far);
    const depthText = `${p.depth.toFixed(2)} ATR`;
    const chart = chartAt(data, m.i, 12, {
      focus: 40,
      emas: [20, 50],
      answerLines: [
        { price: p.extreme, label: t.isLong ? "Recent high" : "Recent low", tone: "muted", dashed: true },
        { price: p.far, label: t.isLong ? "Pullback low" : "Bounce high", tone: "brand", dashed: true },
      ],
      answerMarks: [
        { time: p.extremeTime, label: t.isLong ? "H" : "L", tone: "muted", position: t.isLong ? "above" : "below" },
        { time: p.farTime, label: t.isLong ? "L" : "H", tone: "brand", position: t.isLong ? "below" : "above" },
      ],
    });
    const legWords = t.isLong
      ? "from the highest high of the 7 candles before the last one down to the lowest low of the last 4 candles"
      : "from the lowest low of the 7 candles before the last one up to the highest high of the last 4 candles";
    return {
      kind: "choice",
      prompt: `The desk wants to ${w.buy} this ${w.trend}. Has price made a real pullback? Measure ${legWords}: it needs at least 0.8 ATR.`,
      facts: [
        { label: ATR_FACT, value: price(t.a) },
        { label: "0.8 ATR", value: price(PULLBACK_ATR * t.a) },
      ],
      chart,
      options: [
        { id: "yes", label: "Yes: a real pullback", ...(p.real ? {} : { why: `It only moved ${depthText}: under 0.8 ATR is drift, not a pullback.` }) },
        { id: "no", label: "No: too shallow, wait", ...(p.real ? { why: `It moved ${depthText} from the recent ${w.high}: 0.8 ATR or more counts.` } : {}) },
      ],
      answer: p.real ? "yes" : "no",
      explain: `The recent ${w.high} was ${price(p.extreme)} and the pullback reached ${price(p.far)}: a ${price(move)} move, ${depthText} (ATR ${price(t.a)}). The desk needs at least 0.8 ATR (${price(PULLBACK_ATR * t.a)}), so ${p.real ? "this is a real pullback" : "this is too shallow: wait for a deeper dip"}.`,
      outcome: moveOutcome(last.close, chart.ahead),
    };
  },
};

// ── 6. Trigger or not? ──────────────────────────────────────────────────────

export type TriggerKind = "engulfing" | "pin" | "reclaim" | "none";

export const kindOf = (t: Trigger | null): TriggerKind =>
  t === null ? "none" : t === "level_reclaim" ? "reclaim" : t === "hammer" || t === "shooting_star" ? "pin" : "engulfing";

/**
 * The trigger, or null when nudging any price by `eps` changes it (a shape
 * too close to call by eye). A candle that opens exactly at the last close
 * (the usual case in a 24/7 market) keeps that tie: the two move together,
 * because "opened at or below the last close" is plain to see when they're
 * equal. A near-tie that isn't exact is too close to call.
 */
export function clearTrigger(prev: Candle, last: Candle, isLong: boolean, eps: number): TriggerKind | null {
  const kind = kindOf(reactionTrigger(prev, last, isLong));
  const gap = Math.abs(last.open - prev.close);
  const tied = gap === 0;
  if (!tied && gap < eps) return null;
  const nudges: [Partial<Candle>, Partial<Candle>][] = [];
  for (const d of [eps, -eps]) {
    for (const k of ["open", "high", "low", "close"] as const) {
      if (!(tied && k === "close")) nudges.push([{ [k]: prev[k] + d }, {}]);
      if (!(tied && k === "open")) nudges.push([{}, { [k]: last[k] + d }]);
    }
    if (tied) nudges.push([{ close: prev.close + d }, { open: last.open + d }]);
  }
  for (const [p, l] of nudges) {
    if (kindOf(reactionTrigger({ ...prev, ...p }, { ...last, ...l }, isLong)) !== kind) return null;
  }
  return kind;
}

/** Nudge size for "too close to call": 5% of an ATR. */
const SHAPE_EPS_ATR = 0.05;

export const triggerCandle: GameDef = {
  id: "trigger-candle",
  level: "intermediate",
  title: "Trigger or not?",
  skill: "Name the engine's trigger candle: engulfing, hammer (or shooting star), a close past the last candle, or none yet.",
  needs: { tf: "1h", history: 200, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const want = rng.pick(["engulfing", "pin", "reclaim", "none"] as const);
    const m = pickMoment(data, rng, shown => {
      const t = trendOf(shown);
      if (!t || t.gap < 0.1) return null;
      const kind = clearTrigger(shown[shown.length - 2]!, shown[shown.length - 1]!, t.isLong, SHAPE_EPS_ATR * t.a);
      return kind === null ? null : { t, kind };
    }, r => r.kind === want);
    if (!m) return null;
    const { t, kind } = m.read;
    const isLong = t.isLong;
    const w = sideWords(isLong);
    const prev = m.shown[m.shown.length - 2]!;
    const last = m.shown[m.shown.length - 1]!;
    const b = body(last);
    const pinName = isLong ? "hammer" : "shooting star";
    const engulfName = isLong ? "bullish engulfing" : "bearish engulfing";
    const pastWords = isLong ? `closed above candle 1's high` : `closed below candle 1's low`;
    const level = isLong ? prev.high : prev.low;
    const longWick = isLong ? lowerWick(last) : upperWick(last);
    const shortWick = isLong ? upperWick(last) : lowerWick(last);
    const names: Record<TriggerKind, string> = { engulfing: `a ${engulfName}`, pin: `a ${pinName}`, reclaim: `a close ${isLong ? "above candle 1's high" : "below candle 1's low"}`, none: "no trigger" };
    const order = `The engine checks engulfing first, then the ${pinName}, then the close ${isLong ? "above candle 1's high" : "below candle 1's low"}, and names the first that fits.`;

    const engulfWhy = (): string => {
      if (isLong) {
        if (!isBearish(prev)) return "Candle 1 isn't red, so there's no red body to engulf.";
        if (!isBullish(last)) return "Candle 2 isn't green.";
        if (last.close < prev.open) return `Candle 2 closed at ${price(last.close)}, below candle 1's open (${price(prev.open)}): it doesn't cover candle 1's body.`;
        return `Candle 2 opened at ${price(last.open)}, above candle 1's close (${price(prev.close)}).`;
      }
      if (!isBullish(prev)) return "Candle 1 isn't green, so there's no green body to engulf.";
      if (!isBearish(last)) return "Candle 2 isn't red.";
      if (last.close > prev.open) return `Candle 2 closed at ${price(last.close)}, above candle 1's open (${price(prev.open)}): it doesn't cover candle 1's body.`;
      return `Candle 2 opened at ${price(last.open)}, below candle 1's close (${price(prev.close)}).`;
    };
    const pinWhy = (): string => {
      const isPin = isLong ? isHammer(last) : isShootingStar(last);
      if (isPin) return `It is a ${pinName} too, but the engine checks engulfing first and names that.`;
      if (b <= 0) return "Candle 2 has no body to measure its wicks against.";
      const longSide = isLong ? "lower" : "upper";
      const shortSide = isLong ? "upper" : "lower";
      if (longWick < 0.1 * b) return `It has almost no ${longSide} wick; a ${pinName} needs one at least 2× the body (${price(b)}).`;
      if (longWick < 2 * b) return `Its ${longSide} wick (${price(longWick)}) is only ${(longWick / b).toFixed(1)}× the body (${price(b)}); a ${pinName} needs 2× or more.`;
      return `Its ${shortSide} wick (${price(shortWick)}) is more than half the body (${price(b)}); a ${pinName} has almost no wick on that side.`;
    };
    const reclaimWhy = (): string => {
      const past = isLong ? last.close > prev.high : last.close < prev.low;
      return past
        ? `It did close ${isLong ? "above candle 1's high" : "below candle 1's low"}, but the engine names the first match: ${names[kind]}.`
        : `It closed at ${price(last.close)}, not ${isLong ? "above candle 1's high" : "below candle 1's low"} (${price(level)}).`;
    };
    const why: Record<TriggerKind, string> = {
      pin: pinWhy(),
      engulfing: engulfWhy(),
      reclaim: reclaimWhy(),
      none: `Candle 2 is ${names[kind]}: that is a trigger.`,
    };
    const labels: Record<TriggerKind, string> = {
      pin: `Yes: a ${pinName}`,
      engulfing: `Yes: a ${engulfName}`,
      reclaim: `Yes: it ${pastWords}`,
      none: "No trigger yet",
    };
    const options: Option[] = (["pin", "engulfing", "reclaim", "none"] as const).map(id => (id === kind ? { id, label: labels[id] } : { id, label: labels[id], why: why[id] }));

    const explainFor: Record<TriggerKind, string> = isLong
      ? {
        engulfing: `Candle 1 is red (${price(prev.open)} → ${price(prev.close)}); candle 2 is green, opened at or below ${price(prev.close)} and closed at ${price(last.close)}, at or above candle 1's open: a bullish engulfing.`,
        pin: `Candle 2's lower wick (${price(longWick)}) is ${(longWick / b).toFixed(1)}× its body (${price(b)}) and its upper wick is small: a hammer, buyers pushed price back up.`,
        reclaim: `Candle 2 closed at ${price(last.close)}, above candle 1's high (${price(prev.high)}): buyers took the level back.`,
        none: `Candle 2 isn't an engulfing or a hammer, and it closed at ${price(last.close)}, not above candle 1's high (${price(prev.high)}). No trigger yet: the desk waits for another candle.`,
      }
      : {
        engulfing: `Candle 1 is green (${price(prev.open)} → ${price(prev.close)}); candle 2 is red, opened at or above ${price(prev.close)} and closed at ${price(last.close)}, at or below candle 1's open: a bearish engulfing.`,
        pin: `Candle 2's upper wick (${price(longWick)}) is ${(longWick / b).toFixed(1)}× its body (${price(b)}) and its lower wick is small: a shooting star, sellers pushed price back down.`,
        reclaim: `Candle 2 closed at ${price(last.close)}, below candle 1's low (${price(prev.low)}): sellers took the level.`,
        none: `Candle 2 isn't an engulfing or a shooting star, and it closed at ${price(last.close)}, not below candle 1's low (${price(prev.low)}). No trigger yet: the desk waits for another candle.`,
      };
    const chart = chartAt(data, m.i, 12, {
      focus: 40,
      emas: [20, 50],
      marks: [
        { time: prev.time, label: "1", tone: "muted", position: isLong ? "below" : "above" },
        { time: last.time, label: "2", tone: "brand", position: isLong ? "below" : "above" },
      ],
    });
    return {
      kind: "choice",
      prompt: `The desk wants to ${w.buy} this ${w.trend} (${w.order}). Is candle 2 a trigger, judged against candle 1? Pick the name the engine gives it.`,
      chart,
      options,
      answer: kind,
      explain: `${explainFor[kind]} ${order}`,
      outcome: moveOutcome(last.close, chart.ahead),
    };
  },
};

// ── 7. Place the stop ───────────────────────────────────────────────────────

/** The lowest low (buy) or highest high (sell) of the last 8 candles: the pullback's extreme. */
function stopExtreme(candles: Candle[], isLong: boolean): number {
  const last = candles.slice(-STOP_LOOKBACK);
  return isLong ? Math.min(...last.map(c => c.low)) : Math.max(...last.map(c => c.high));
}

export const engineStop: GameDef = {
  id: "engine-stop",
  level: "intermediate",
  title: "Place the stop",
  skill: "Put the stop where the engine does: 0.25 ATR past the last 8 candles' extreme.",
  needs: { tf: "1h", history: 120, ahead: 24 },
  make(data) {
    if (!data) return null;
    const c = data.candles;
    const t = trendOf(c);
    // Only in a clear trend, so the side is obvious from the EMAs.
    if (!t || t.gap < 0.2) return null;
    const { isLong, a } = t;
    const w = sideWords(isLong);
    const entry = c[c.length - 1]!.close;
    const stop = pullbackStop(c, isLong, a);
    const extreme = stopExtreme(c, isLong);
    if (Math.abs(entry - stop) < 0.5 * a) return null;
    // Right: past the extreme, and not more than half an ATR beyond the engine's stop.
    const accept = isLong ? { from: stop - 0.5 * a, to: extreme } : { from: extreme, to: stop + 0.5 * a };
    const risk = Math.abs(entry - stop);
    const target = entry + (isLong ? 1 : -1) * TP1_FALLBACK_R * risk;
    const outcome = planOutcome(isLong ? "LONG" : "SHORT", entry, stop, target, data.ahead);
    return {
      kind: "price",
      prompt: `The desk ${w.buy}s this ${w.trend} at the last close (${price(entry)}). Click where the engine puts the stop.`,
      facts: [{ label: ATR_FACT, value: price(a) }],
      chart: {
        tf: data.tf,
        candles: c,
        ahead: data.ahead,
        focus: 50,
        emas: [20, 50],
        lines: [{ price: entry, label: "Entry", tone: "brand" }],
        answerLines: [
          { price: extreme, label: isLong ? "8-candle low" : "8-candle high", tone: "muted", dashed: true },
          { price: stop, label: "Stop", tone: "loss" },
        ],
      },
      answer: stop,
      accept,
      explain: `The stop goes 0.25 ATR (${price(STOP_BUFFER_ATR * a)}) past the ${isLong ? "lowest low" : "highest high"} of the last 8 candles (${price(extreme)}): ${price(stop)}. If price gets there, the pullback has failed. Anywhere from that ${w.low} to half an ATR past the engine's stop counts.`,
      outcome: outcome ? `${outcome} (Target 1 at ${rText(TP1_FALLBACK_R)}.)` : undefined,
    };
  },
};

// ── 8. Place target 1 ───────────────────────────────────────────────────────

export const targetOne: GameDef = {
  id: "target-one",
  level: "intermediate",
  title: "Place target 1",
  skill: "Measure the engine's targets in R: target 1 at 1.5R when no level is in the way, target 2 at 2.5R.",
  needs: { tf: "1h", history: 120, ahead: 48 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    const t = trendOf(c);
    if (!t || t.gap < 0.1) return null;
    const { isLong, a } = t;
    const w = sideWords(isLong);
    const dir = isLong ? 1 : -1;
    const entry = c[c.length - 1]!.close;
    const stop = pullbackStop(c, isLong, a);
    const risk = Math.abs(entry - stop);
    if (risk < 0.5 * a) return null;
    const second = rng.chance(0.5);
    const mult = second ? TP2_R : TP1_FALLBACK_R;
    const target = entry + dir * mult * risk;
    if (!(target > 0)) return null;
    const lo = Math.min(...c.map(k => k.low));
    const hi = Math.max(...c.map(k => k.high));
    if (target < lo - 0.4 * (hi - lo) || target > hi + 0.4 * (hi - lo)) return null;
    const name = second ? "target 2" : "target 1";
    const outcome = planOutcome(isLong ? "LONG" : "SHORT", entry, stop, target, data.ahead);
    const math = `${price(entry)} ${isLong ? "+" : "−"} ${mult} × ${price(risk)} = ${price(target)}`;
    return {
      kind: "price",
      prompt: second
        ? `You ${w.buy} at ${price(entry)} with the engine's stop at ${price(stop)}. Click where the engine puts target 2.`
        : `You ${w.buy} at ${price(entry)} with the engine's stop at ${price(stop)}. Click target 1: there's no level in the way.`,
      facts: [
        { label: "Entry", value: price(entry) },
        { label: "Stop", value: price(stop) },
        { label: "1R (entry to stop)", value: price(risk) },
      ],
      chart: {
        tf: data.tf,
        candles: c,
        ahead: data.ahead,
        focus: 60,
        emas: [20, 50],
        lines: [
          { price: entry, label: "Entry", tone: "brand" },
          { price: stop, label: "Stop", tone: "loss" },
        ],
        answerLines: [{ price: target, label: second ? "Target 2" : "Target 1", tone: "profit" }],
      },
      answer: target,
      accept: { from: target - 0.15 * risk, to: target + 0.15 * risk },
      explain: second
        ? `R is one unit of the risk you planned: ${price(entry)} to ${price(stop)} = ${price(risk)}. Target 2 is always 2.5R: ${math}. Within 0.15R counts.`
        : `R is one unit of the risk you planned: ${price(entry)} to ${price(stop)} = ${price(risk)}. With no level in the way, target 1 is 1.5R: ${math}. Within 0.15R counts.`,
      outcome: outcome || undefined,
    };
  },
};

// ── 9. RSI in the band? ─────────────────────────────────────────────────────

export type RsiClass = "a" | "b" | "out";

/** The trend-pullback RSI grade: in the A band, only in the wider B+ band, or outside both (no trade). */
export function rsiClass(rsi: number, isLong: boolean): RsiClass {
  const band = RSI_BAND[isLong ? "LONG" : "SHORT"];
  if (rsi >= band.a[0] && rsi <= band.a[1]) return "a";
  if (rsi >= band.b[0] && rsi <= band.b[1]) return "b";
  return "out";
}

export const rsiBand: GameDef = {
  id: "rsi-band",
  level: "intermediate",
  title: "RSI in the band?",
  skill: "Grade a pullback's RSI against the engine's A band and wider B+ band.",
  needs: { tf: "1h", history: 220, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const want = rng.pick(["a", "b", "out"] as const);
    const m = pickMoment(data, rng, shown => {
      const t = trendOf(shown);
      const r = rsiLast(shown);
      if (!t || t.gap < 0.1 || r === null) return null;
      const band = RSI_BAND[t.isLong ? "LONG" : "SHORT"];
      if ([...band.a, ...band.b].some(x => Math.abs(r - x) < 0.5)) return null;
      return { t, r, cls: rsiClass(r, t.isLong) };
    }, x => x.cls === want);
    if (!m) return null;
    const { t, r, cls } = m.read;
    const w = sideWords(t.isLong);
    const band = RSI_BAND[t.isLong ? "LONG" : "SHORT"];
    const aText = `${band.a[0]}–${band.a[1]}`;
    const bText = `${band.b[0]}–${band.b[1]}`;
    const rText1 = r.toFixed(1);
    const outWhy = r > band.b[1]
      ? (t.isLong ? "momentum is overheated: the bounce already ran" : "momentum is still too strong upward for a sell")
      : (t.isLong ? "momentum is still falling too hard for a buy" : "momentum is washed out: the drop already ran");
    const verdict = cls === "a"
      ? `${rText1} is inside the A band, the best grade`
      : cls === "b"
        ? `${rText1} is outside the A band but inside the B+ band: the trade is still allowed, graded B+`
        : `${rText1} is outside both, ${outWhy}, so the engine skips it`;
    const last = m.shown[m.shown.length - 1]!;
    const chart = chartAt(data, m.i, 12, { focus: 60, emas: [20, 50] });
    return {
      kind: "choice",
      prompt: `The desk wants to ${w.buy} this ${w.trend}. Is RSI in the setup's band?`,
      facts: [
        { label: "Side", value: `${w.BUY} (${w.trend})` },
        { label: "RSI 14", value: rText1 },
      ],
      chart,
      options: [
        { id: "a", label: `Yes: in the A band (${aText} for a ${w.buy})`, ...(cls === "a" ? {} : { why: `${rText1} is outside ${aText}.` }) },
        { id: "b", label: `Only in the wider B+ band (${bText})`, ...(cls === "b" ? {} : { why: cls === "a" ? `${rText1} is inside the stricter A band too, so it gets the better grade.` : `${rText1} is outside ${bText} as well.` }) },
        { id: "out", label: "No: outside both, no trade", ...(cls === "out" ? {} : { why: `${rText1} is inside ${bText}: the engine still takes it.` }) },
      ],
      answer: cls,
      explain: `RSI (a 0 to 100 momentum gauge) is ${rText1}. For a ${w.buy} the A band is ${aText} and the wider B+ band ${bText}: ${verdict}.`,
      outcome: moveOutcome(last.close, chart.ahead),
    };
  },
};

// ── 10. Enough volume? ──────────────────────────────────────────────────────

export type VolumeClass = "strong" | "enough" | "weak";

export function volumeClass(ratio: number): VolumeClass {
  return ratio >= VOLUME_STRONG ? "strong" : ratio >= VOLUME_MIN ? "enough" : "weak";
}

/** A volume as text: "12,345", "842.5", "3.271". */
function volText(n: number): string {
  const d = n >= 100 ? 0 : n >= 1 ? 1 : 3;
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

export const volumeConfirms: GameDef = {
  id: "volume-confirms",
  level: "intermediate",
  title: "Enough volume?",
  skill: "Check the trigger candle's volume against the 20 candles before it: under 0.5× doesn't count, 1.2× or more is strong.",
  needs: { tf: "1h", history: 150, ahead: 12 },
  make(data, rng) {
    if (!data) return null;
    const want = rng.pick(["strong", "enough", "weak"] as const);
    const m = pickMoment(data, rng, shown => {
      const t = trendOf(shown);
      const ratio = volumeRatio(shown);
      if (!t || t.gap < 0.1 || ratio === null) return null;
      if (Math.abs(ratio - VOLUME_MIN) < 0.05 || Math.abs(ratio - VOLUME_STRONG) < 0.05) return null;
      // The question is about a trigger candle, so the moment must have one.
      const trig = reactionTrigger(shown[shown.length - 2]!, shown[shown.length - 1]!, t.isLong);
      if (!trig) return null;
      return { t, ratio, trig, cls: volumeClass(ratio) };
    }, x => x.cls === want);
    if (!m) return null;
    const { t, ratio, trig, cls } = m.read;
    const w = sideWords(t.isLong);
    const last = m.shown[m.shown.length - 1]!;
    const before = m.shown.slice(-21, -1);
    const avg = before.reduce((s, c) => s + c.volume, 0) / before.length;
    const trigName = trig === "level_reclaim"
      ? (t.isLong ? "close above the previous candle's high" : "close below the previous candle's low")
      : trig.replace("_", " ");
    const ratioText = `${ratio.toFixed(2)}×`;
    const chart = chartAt(data, m.i, 12, {
      focus: 40,
      emas: [20, 50],
      volume: true,
      marks: [{ time: last.time, label: "T", tone: "brand", position: t.isLong ? "below" : "above" }],
    });
    return {
      kind: "choice",
      prompt: `Candle T is a ${w.buy} trigger (a ${trigName}). Does it have enough volume for the desk?`,
      facts: [
        { label: "Volume of candle T", value: volText(last.volume) },
        { label: "Average of the 20 before", value: volText(avg) },
      ],
      chart,
      options: [
        { id: "strong", label: "Strong: 1.2× average or more (A+)", ...(cls === "strong" ? {} : { why: `It's ${ratioText} the average, under 1.2×.` }) },
        { id: "enough", label: "Enough: 0.5× to 1.2×", ...(cls === "enough" ? {} : { why: cls === "strong" ? `It's ${ratioText}: that's 1.2× or more, strong.` : `It's ${ratioText}: under 0.5× doesn't count.` }) },
        { id: "weak", label: "Too weak: under 0.5×, the engine skips it", ...(cls === "weak" ? {} : { why: `It's ${ratioText} the average, at least 0.5×.` }) },
      ],
      answer: cls,
      explain: `${volText(last.volume)} ÷ ${volText(avg)} (the 20 candles before it) = ${ratioText} the average. Under 0.5× the engine skips the trade, 0.5× and up counts, and 1.2× or more is strong (one of the A+ conditions).`,
      outcome: moveOutcome(last.close, chart.ahead),
    };
  },
};

export const GAMES: GameDef[] = [
  fourHourBias,
  withTheBias,
  inTheZone,
  clickTheZone,
  realPullback,
  triggerCandle,
  engineStop,
  targetOne,
  rsiBand,
  volumeConfirms,
];
